import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { CancelTokenSource } from 'axios';
import { CheckboxSc } from 'components/final-form/CheckBox';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import { MandateConsumerRenderProps, withMandateContext } from 'components/MandateContext';
import UpsertPatient from 'components/patient/upsert/UpsertPatient';
import moment from 'moment';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouteComponentProps } from 'react-router';
import { Button, Icon, Loader, Popup } from 'semantic-ui-react';
import { getAllCountries } from 'services/countryServices';
import axios from 'services/http';
import { getAllLanguages } from 'services/languageServices';
import { downloadXml, sendToMediData, updateBillXmlRequest } from 'services/mediDataServices';
import { findPatientById } from 'services/patientServices';
import styled from 'styled-components';
import { BillXmlRequestType } from 'ts-types/api.enums';
import { BillXmlRequestDto, CountryDto, LanguageDto, PatientDto, UpsertPatientDto } from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { noop } from 'utils/functionUtils';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const TableDiv = styled.div`
  flex: 1 1 auto;
  padding: 0.5rem;
  background: white;
`;

const ActionsDiv = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  background: white;
`;

const SendSuccessDiv = styled.div`
  flex: 1 1 auto;
  margin-left: 1.25rem;
  color: green;
`;

const SendErrorDiv = styled.div`
  flex: 1 1 auto;
  margin-left: 1.25rem;
  color: darkred;
`;

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34, 36, 38, .15);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }

  & .evenRow:hover, .oddRow:hover {
    background: rgba(0, 0, 0, .05);
  }
`;

const StatusCell = styled.div`
  white-space: nowrap;
`;

const PatientName = styled.div`
  font-size: 0.95rem;
`;

const PatientData = styled.div`
  font-size: 0.9rem;
  color: #575757;
`;

const NoDataDiv = styled.div`
  font-size: 1rem;
  color: #444;
  font-style: italic;
  padding: 0.7rem 1rem;
`;

const UpdateXmlSuccess = styled.p`
  color: green;
  width: 100%;
  height: 100%;
  white-space: initial;
`;

const UpdateXmlError = styled.p`
  color: red;
  width: 100%;
  height: 100%;
  white-space: initial;
`;

const StyledLoader = styled(Loader)`
  margin-left: .7em !important;
  vertical-align: bottom !important;

  &:before {
    border: .2em solid rgba(255, 2555, 255, .4) !important;
  }

  &:after {
    border-color: #ffffff transparent transparent !important;
  }
`;

const errorBtnStyle = {
  background: 'transparent',
  cursor: 'pointer',
  padding: '2px 5px',
  color: 'red'
};

interface UpdateBillXmlResponse {
  message: string,
  success: boolean,
  billXmlRequestId: number
}

interface ExtendedBillXmlRequestDto extends BillXmlRequestDto {
  checked: boolean
}

enum AllChosenState {
  ALL,
  SOME,
  NONE
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps, RouteComponentProps, MandateConsumerRenderProps {
  bills: Array<BillXmlRequestDto>,
  billsTariffId: number,
  billsKeyInt: number,
  onBillsUpdated: (bills: Array<BillXmlRequestDto>) => void,
  billsLoading: boolean,
  preselectBills: boolean,
  onClose?: () => void
}

interface State {
  mandateId: number,

  bills: Array<ExtendedBillXmlRequestDto>,
  allChosenState: AllChosenState,

  sendError?: string,

  patientId?: number,
  patient?: UpsertPatientDto,
  patientModalOpen: boolean,
  patientLoading: boolean,

  countries: Array<CountryDto>,
  languages: Array<LanguageDto>,

  cancelTokenSource: CancelTokenSource,

  updateBillXmlResponse?: UpdateBillXmlResponse,

  sendingToMedidata: boolean,
  sendSuccess: boolean
}

class MediDataUploadTableView extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {currentUser, preselectBills = false} = this.props;

    const mappedBills: Array<ExtendedBillXmlRequestDto> = this.props.bills.map(bill => {
      const extendedBill = {
        ...bill,
        checked: false
      };

      extendedBill.checked = this.billChoosable(extendedBill) && preselectBills;

      return extendedBill;
    });

    const cancelTokenSource = axios.CancelToken.source();

    getAllCountries(cancelTokenSource).then(response => {
      this.setState({
        countries: response
      });
    }).catch(noop);

    getAllLanguages(cancelTokenSource).then(response => {
      this.setState({
        languages: response
      });
    }).catch(noop);

    this.state = {
      mandateId: currentUser!.userConfig.mandateId,
      bills: mappedBills,
      allChosenState: preselectBills ? AllChosenState.ALL : AllChosenState.NONE,
      patientModalOpen: false,
      countries: [],
      languages: [],
      patientLoading: false,
      sendingToMedidata: false,
      sendSuccess: false,
      cancelTokenSource
    }
  }

  componentDidUpdate(
      {billsKeyInt: prevBillsKeyInt, bills: prevBills}: Readonly<Props>,
      prevState: Readonly<State>,
      snapshot?: any
  ): void {


    if (prevBillsKeyInt !== this.props.billsKeyInt) {
      const mappedBills: Array<ExtendedBillXmlRequestDto> = this.props.bills.map(bill => {

        return {
          ...bill,
          checked: false
        }
      });

      this.setState({
        bills: mappedBills
      })
    }
  }

  updateBillXmlRequest = (billXmlRequestId: number) => {
    const {getMessage} = this.props;
    updateBillXmlRequest(billXmlRequestId, this.state.cancelTokenSource)
        .then(response => {
          // this.props.onBillsUpdated(response);

          const updatedBillsMap: { [key: number]: BillXmlRequestDto } = {};
          response.forEach(bill => {
            updatedBillsMap[bill.id] = bill;
          });

          const {bills} = this.state;
          const newBills: Array<ExtendedBillXmlRequestDto> = bills.map(bill => {

            const updatedBill = updatedBillsMap[bill.id];
            if (updatedBill) {
              return {
                ...updatedBill,
                checked: bill.checked
              };
            }

            return bill;
          });

          this.setState({
            bills: newBills
          });

          this.setUpdateXmlMessage(
              billXmlRequestId,
              getMessage("mediData.tableAction.updateXml.success"),
              true);

        })
        .catch(() => {
          this.setUpdateXmlMessage(billXmlRequestId, getMessage("error.general"), false);
        })
        .finally(() => {
          // onBillsLoadingChange(false);
        })
  };

  setSendSuccess = (sendSuccess: boolean) => {
    setTimeout(
        () => {
          this.setState({
            sendSuccess: sendSuccess
          })
        }, 2000);
  };

  sendBillXmlRequests = () => {

    const {mandateId, bills, cancelTokenSource} = this.state;

    const billXmlIds = bills.filter(bill => bill.checked).map(bill => bill.id);

    this.setState({
      sendError: undefined,
      sendingToMedidata: true
    });

    sendToMediData(mandateId, billXmlIds, cancelTokenSource)
        .then(response => {

          const updatedBillsMap: { [key: number]: BillXmlRequestDto } = {};
          response.billXmlRequests.forEach(bill => {
            updatedBillsMap[bill.id] = bill;
          });

          const {bills} = this.state;
          const newBills: Array<ExtendedBillXmlRequestDto> = bills.map(bill => {

            const updatedBill = updatedBillsMap[bill.id];
            if (updatedBill) {
              return {
                ...updatedBill,
                checked: false
              };
            }

            return {
              ...bill,
              checked: false
            };
          });

          this.setState({
            bills: newBills,
            allChosenState: AllChosenState.NONE,
            sendSuccess: true
          });
          this.setSendSuccess(false);
        })
        .catch(error => {
          this.handleSendError(error.response.data);
        })
        .finally(() => {
          this.setState({
            sendingToMedidata: false
          })
        });
  };

  handleSendError(error: any) {
    const {getMessage} = this.props;

    const errorCode = error.errorCode;
    const knownErrors = [errorUtils.noMandateTariffConfig];
    if (knownErrors.includes(errorCode)) {
      this.setState({
        sendError: getMessage(`mediData.error.${errorCode}`)
      });
    } else {
      this.setState({
        sendError: getMessage('error.general')
      })
    }
  }

  render(): React.ReactNode {
    const {
      bills,
      sendError,
      patient,
      patientId,
      patientModalOpen,
      countries,
      languages,
      sendingToMedidata,
      patientLoading,
      sendSuccess
    } = this.state;

    const {getMessage, billsLoading, currentMandates, getAllMandates, onClose} = this.props;

    const countCheckedBills: number = bills.filter(b => b.checked).length;
    const disableSubmit = !(bills && bills.length > 0 && countCheckedBills > 0);

    if (billsLoading) {
      return (
          <Loader active={true} size="small">
            {`${getMessage("loading")} ${getMessage("bills").toLowerCase()}`}
          </Loader>
      );
    }

    return (
        <>
          <TableDiv>
            {sendingToMedidata
                ? <LoaderComponent message={getMessage("mediData.saving")}/>
                : (bills.length > 0)
                    ? this.renderTable(bills)
                    : (
                        <NoDataDiv>No XML bills found.</NoDataDiv>
                    )
            }
          </TableDiv>
          <ActionsDiv>
            <Button compact type="button" disabled={disableSubmit} primary onClick={this.sendBillXmlRequests}>
              {getMessage("mediData.sendToMediData")}
              {
                sendingToMedidata &&
                <StyledLoader size="mini" active inline/>
              }
            </Button>

            {
              sendSuccess && (
                  <SendSuccessDiv>
                    {getMessage("mediData.sendSuccess")}
                  </SendSuccessDiv>
              )}

            {
              sendError && (
                  <SendErrorDiv>
                    {sendError}
                  </SendErrorDiv>
              )}
            {
              onClose &&
              <Button compact type="button" floated="right" onClick={onClose}>
                {getMessage("button.close")}
              </Button>
            }
          </ActionsDiv>
          {
            patientLoading &&
            <LoaderComponent message={getMessage("patient.loading.single")}/>
          }
          {
            patientModalOpen &&
            <UpsertPatient
                modalOpen={patientModalOpen}
                toggleModal={this.togglePatientModal}
                countries={countries}
                languages={languages}
                patient={patient}
                patientId={patientId}
                onPatientSave={this.onPatientSave}
                currentMandates={currentMandates}
                getAllMandates={getAllMandates}
            />
          }
        </>
    );
  }

  renderTable = (bills: Array<ExtendedBillXmlRequestDto>): JSX.Element => {

    const {getMessage} = this.props;

    return (
        <StyledTable
            rowGetter={this.billsRowGetter(bills)}
            rowRenderer={this.billsRowRenderer(bills)}
            rowCount={bills.length}
            rowHeight={52}
            columns={[
              {
                width: 80,
                flexGrow: 0,
                flexShrink: 0,
                label: this.allChosenHeaderColumn(bills),
                dataKey: "checked",
                cellRenderer: this.checkedCellRenderer
              },
              {
                width: 100,
                flexGrow: 0,
                flexShrink: 0,
                label: getMessage("mediData.tableHeader.createdDate"),
                dataKey: "createdDate",
                cellRenderer: this.dateCellRenderer
              },
              {
                width: 50,
                flexGrow: 0,
                flexShrink: 0,
                label: getMessage("mediData.tableHeader.type"),
                dataKey: "type",
                cellRenderer: this.billXmlTypeCellRenderer
              },
              {
                width: 180,
                flexGrow: 0,
                flexShrink: 0,
                label: getMessage("mediData.tableHeader.status"),
                dataKey: "status",
                cellRenderer: this.statusCellRenderer
              },
              {
                width: 180,
                flexGrow: 0,
                flexShrink: 0,
                label: getMessage("mediData.tableHeader.mediDataStatus"),
                dataKey: "mediDataRemoteStatus",
                cellRenderer: this.mediDataStatusCellRenderer
              },
              {
                width: 100,
                flexGrow: 0,
                flexShrink: 0,
                label: getMessage("mediData.tableHeader.transmissionDate"),
                dataKey: "transmissionDate",
                cellRenderer: this.dateCellRenderer
              },
              {
                width: 150,
                flexGrow: 1,
                flexShrink: 0,
                label: getMessage("mediData.tableHeader.billPatient"),
                dataKey: "firstName",
                cellRenderer: this.xmlPatientCellRenderer
              },
              {
                width: 150,
                flexGrow: 1,
                flexShrink: 0,
                label: getMessage("mediData.tableHeader.appPatient"),
                dataKey: "firstName",
                cellRenderer: this.appPatientCellRenderer
              },
              {
                width: 120,
                flexGrow: 0,
                flexShrink: 0,
                label: getMessage("mediData.tableHeader.rowActions"),
                dataKey: "firstName",
                cellRenderer: this.rowActionsCellRenderer
              }
            ]}
        />
    )
  };

  allChosenHeaderColumn = (bills: Array<ExtendedBillXmlRequestDto>) => {

    const allChosenState = this.state.allChosenState;

    const countBills = bills.length;
    const countChoosableBills = bills.filter(bill => !bill.error && bill.status === 'GENERATED').length;
    const countSelectedBills = bills.filter(b => b.checked).length;

    return (
        <CheckboxSc
            checked={allChosenState === AllChosenState.ALL}
            indeterminate={allChosenState === AllChosenState.SOME}
            onChange={
              () => {
                const currentState = this.state.allChosenState;
                let nextState = AllChosenState.NONE;
                let bills: Array<ExtendedBillXmlRequestDto>;
                if (currentState === AllChosenState.ALL || currentState === AllChosenState.SOME) {
                  nextState = AllChosenState.NONE;
                  bills = this.state.bills!.map(bill => ({...bill, checked: false}));
                } else {
                  nextState = AllChosenState.ALL;
                  bills = this.state.bills!.map(bill => ({...bill, checked: this.billChoosable(bill)}));
                }

                this.setState({
                  allChosenState: nextState,
                  bills
                })
              }
            }
            label={`${countSelectedBills} / ${countBills}`}
            disabled={countChoosableBills === 0}
        />
    )
  };

  billsRowGetter = (bills: Array<ExtendedBillXmlRequestDto>) => ({index}: any) => {

    if (bills[index]) {
      Object.assign(bills[index], {index: index + 1});

      return bills[index];
    }

    return {
      index: index + 1
    }
  };

  billsRowRenderer = (bills: Array<ExtendedBillXmlRequestDto>) =>
      ({className, columns, index, key, style}: any) => {

        const a11yProps = {"aria-rowindex": index + 1};

        let styles = {...style};

        const {billId, checked} = bills[index];

        return (
            <div
                {...a11yProps}
                className={className}
                key={key}
                role="row"
                style={styles}
            >
              {columns}
            </div>
        );
      };

  checkedCellRenderer = (data: any) => {

    const {rowData} = data;

    const checkEnabled = this.billChoosable(rowData);
    return (
        <CheckboxSc checked={rowData.checked}
                    disabled={!checkEnabled}
                    label={rowData.billId}
                    onClick={() => {
                      if (checkEnabled) {
                        this.toggleBill(rowData)
                      }
                    }}
        />
    )
  };

  billXmlTypeCellRenderer = (data: any) => {
    const {getMessage} = this.props;
    const {rowData} = data;

    let typeIcon = (
        <span title={getMessage("mediData.billXmlType.invoice")}>
          <Icon name="file alternate outline" size="small"/>
        </span>
    );
    switch (rowData.type as BillXmlRequestType) {
      case BillXmlRequestType.REMINDER_1: {
        typeIcon = (
            <span title={getMessage("mediData.billXmlType.reminder")}>
              <Icon name="bell outline" size="small"/>
            </span>
        );
        break;
      }
      case BillXmlRequestType.REMINDER_2: {
        typeIcon = (
            <span title={getMessage("mediData.billXmlType.reminder") + " (2)"}>
              <Icon name="bell outline" size="small"/>
              <Icon name="bell outline" size="small"/>
            </span>
        );
        break;
      }
      case BillXmlRequestType.REMINDER_3: {
        typeIcon = (
            <span title={getMessage("mediData.billXmlType.reminder") + " (3)"}>
              <Icon name="bell outline" size="small"/>
              <Icon name="bell outline" size="small"/>
              <Icon name="bell outline" size="small"/>
            </span>
        );
        break;
      }
    }

    return (
        <span style={{whiteSpace: "nowrap"}}>
          {typeIcon}
          {
            rowData.cancellation
                ? (
                    <span title={getMessage("mediData.billXmlType.storno")}>
                      <Icon name="delete" color="red" size="small"/>
                    </span>
                )
                : null
          }
        </span>
    )
  };

  statusCellRenderer = (data: any) => {

    const {rowData} = data;

    return (
        <StatusCell>
          <span>{rowData.status}</span>
          {
            rowData.error && (
                <Popup
                    trigger={this.errorPopupTrigger()}
                    content={this.errorPopupContent(rowData)}
                    on="click"
                    size="small"
                    position="bottom center"
                />
            )
          }
        </StatusCell>
    )
  };

  mediDataStatusCellRenderer = (data: any) => {

    const {cellData} = data;

    return (
        <StatusCell>
          <span>{cellData}</span>
        </StatusCell>
    )
  };

  appPatientCellRenderer = (data: any) => {

    const {getMessage} = this.props;
    const {rowData} = data;

    const genderKey = (rowData.genderDb === "female") ? "gender.female" : "gender.male";

    return (
        <div>
          <a onClick={() => this.openPatientModal(rowData.patientId)}>
            <PatientName>{rowData.firstNameDb + ' ' + rowData.lastNameDb}</PatientName>
          </a>
          <PatientData>
            <span>{moment(rowData.birthDateDb).format("DD.MM.YYYY")}</span>&nbsp;|&nbsp;
            <span>{getMessage(genderKey)}</span>
          </PatientData>
        </div>
    )
  };

  xmlPatientCellRenderer = (data: any) => {

    const {getMessage} = this.props;
    const {rowData} = data;

    const genderKey = (rowData.gender === "female") ? "gender.female" : "gender.male";

    return (
        <div>
          <PatientName>{rowData.firstName + ' ' + rowData.lastName}</PatientName>
          <PatientData>
            <span>{moment(rowData.birthDate).format("DD.MM.YYYY")}</span>&nbsp;|&nbsp;
            <span>{getMessage(genderKey)}</span>
          </PatientData>
        </div>
    )
  };

  rowActionsCellRenderer = (data: any) => {

    const {rowData} = data;
    const {getMessage} = this.props;
    const {updateBillXmlResponse} = this.state;

    const renderTarget = (
        <div>
          <a>{getMessage('mediData.tableAction.updateXml')}</a>
        </div>
    );

    // No need to update the XML any more
    // return (updateBillXmlResponse && updateBillXmlResponse.billXmlRequestId === rowData.id) ?
    //     this.renderUpdateXmlResponse() :
    //     (
    //         <ConfirmActionPopup
    //             message={getMessage("mediData.tableAction.updateXml.confirm")}
    //             renderTarget={renderTarget}
    //             onConfirmAction={() => this.updateBillXmlRequest(rowData.id)}
    //         />
    //     );

    return <div>
      <Icon name="download" size="large" onClick={() => {
        downloadXml(rowData.id, this.state.cancelTokenSource);
      }}/>
    </div>;
  };

  renderUpdateXmlResponse = () => {
    const {updateBillXmlResponse} = this.state;
    const updateBillXmlResponseFixed = updateBillXmlResponse!;

    return updateBillXmlResponseFixed.success ?
        (
            <UpdateXmlSuccess>
              {
                updateBillXmlResponseFixed.message
              }
            </UpdateXmlSuccess>
        ) :
        (
            <UpdateXmlError>
              {
                updateBillXmlResponseFixed.message
              }
            </UpdateXmlError>
        );
  };

  errorPopupTrigger() {
    return (
        <Button type="button" icon style={errorBtnStyle}>
          <Icon name="warning sign" size="small"/>
        </Button>
    );
  };

  errorPopupContent(bill: ExtendedBillXmlRequestDto) {

    return (
        <div>{bill.validationError}</div>
    )
  };

  toggleBill = (rowData: ExtendedBillXmlRequestDto) => {
    const stateBills: Array<ExtendedBillXmlRequestDto> = this.state.bills;
    const bills = stateBills.map(
        bill => (bill.id !== rowData.id) ? bill : {...bill, checked: !bill.checked});

    const chosenBill = bills.find(bill => bill.checked);
    const unchosenBill = bills.find(bill => !bill.checked);

    let allChosenState = AllChosenState.SOME;
    if (!unchosenBill) {
      allChosenState = AllChosenState.ALL;
    } else if (!chosenBill) {
      allChosenState = AllChosenState.NONE;
    }

    this.setState({
      bills,
      allChosenState
    });
  };

  billChoosable(bill: ExtendedBillXmlRequestDto): boolean {
    return !bill.error && bill.status === 'GENERATED';
  }

  billCreatedDateDateCellRenderer = (data: any) =>
      <div>{moment(data.rowData.createdDate).format("DD.MM.YYYY")}</div>;

  dateCellRenderer = (data: any) => {
    const date: string | undefined = data.rowData[data.dataKey];
    if (!date) {
      return <></>;
    }
    return (<div>{moment(date).format("DD.MM.YYYY")}</div>);
  };

  setUpdateXmlMessage = (billXmlRequestId: number, message: string, success: boolean) => {
    const updateBillXmlResponse: UpdateBillXmlResponse = {
      billXmlRequestId, message, success
    };
    this.setState({updateBillXmlResponse});

    setTimeout(
        () => {
          this.setState({
            updateBillXmlResponse: undefined
          })
        }, 2000);
  };

  openPatientModal = (patientId: number) => {
    const {cancelTokenSource} = this.state;

    this.setState({patientLoading: true});
    findPatientById(cancelTokenSource, patientId)
        .then(response => {
          this.setState({
            patientId: patientId,
            patient: response
          });
          this.togglePatientModal();
        })
        .catch(noop)
        .finally(() => this.setState({patientLoading: false}));
  };

  togglePatientModal = () => {
    this.setState(({patientModalOpen}: State) => ({
      patientModalOpen: !patientModalOpen
    }));
  };

  onPatientSave = (patient: PatientDto) => {
    const {bills} = this.state;
    const newBills: Array<ExtendedBillXmlRequestDto> = bills.map(bill => {

      if (bill.patientId === patient.id) {
        return {
          ...bill,
          firstNameDb: patient.firstName,
          lastNameDb: patient.name,
          birthDateDb: patient.birthDate,
          genderDb: patient.sex === 1 ? "female" : "male"
        };
      }

      return bill;
    });

    this.setState({
      bills: newBills
    });

    this.togglePatientModal();
  };

}

export default withMessages(withAuthContext(withRouterWorkaround(withMandateContext(MediDataUploadTableView))));
